import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { resolveAssetUrl } from '../lib/utils';
import ExistingClientQuickStart from './ExistingClientQuickStart';
import ContextHelpButton from './ContextHelpButton';
import {
  Plus, Eye, PencilSimple, Trash, Lightning, Article, Archive, ImageSquare, Check, X, UsersThree, ArrowRight
} from '@phosphor-icons/react';

export default function Dashboard({ setActiveView, setSelectedProject, setWizardResumeData }) {
  const { api } = useAuth();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingClientId, setDeletingClientId] = useState('');
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingProjectName, setEditingProjectName] = useState('');
  const [renamingProjectId, setRenamingProjectId] = useState(null);
  const [showExistingClientQuickStart, setShowExistingClientQuickStart] = useState(false);
  const [quickStartClientId, setQuickStartClientId] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get('/projects').catch(() => ({ data: [] })),
      api.get('/client-brains').catch(() => ({ data: [] })),
    ]).then(([projectResponse, clientResponse]) => {
      if (cancelled) return;
      setProjects(projectResponse.data || []);
      setClients(clientResponse.data || []);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [api]);

  const deleteProject = async (id) => {
    if (!window.confirm(t('dashboard.deleteProjectConfirm'))) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      alert('Errore eliminazione: ' + (e.response?.data?.detail || e.message));
    }
  };

  const archiveProject = async (id, archived) => {
    try {
      await api.post(`/projects/${id}/${archived ? 'unarchive' : 'archive'}`);
      setProjects(prev => prev.map(p => p.id === id ? { ...p, archived: !archived } : p));
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message = detail && typeof detail === 'object' ? detail.message : detail;
      toast.error(message || e.message);
    }
  };

  const openProject = async (project) => {
    try {
      const { data } = await api.get(`/projects/${project.id}`);
      setSelectedProject(data);
      setActiveView('content');
    } catch {
      setSelectedProject(project);
      setActiveView('content');
    }
  };

  const resumeWizard = async (project) => {
    try {
      const { data } = await api.get(`/projects/${project.id}/wizard-state`);
      setWizardResumeData(data);
      setActiveView('wizard');
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
  };

  const openClientCampaign = (clientId) => {
    setQuickStartClientId(clientId || '');
    setShowExistingClientQuickStart(true);
  };

  const deleteClient = async (client) => {
    if (!client?.id || deletingClientId) return;
    const linkedCount = projects.filter(project => project.client_brain_id === client.id).length;
    const confirmed = window.confirm(
      t('dashboard.deleteClientConfirm', { name: client.name, count: linkedCount })
    );
    if (!confirmed) return;

    setDeletingClientId(client.id);
    try {
      await api.delete(`/client-brains/${client.id}`);
      setClients(prev => prev.filter(item => item.id !== client.id));
      setProjects(prev => prev.map(project => project.client_brain_id === client.id ? { ...project, client_brain_id: null } : project));
      toast.success(t('dashboard.deleteClientSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('dashboard.deleteClientError'));
    } finally {
      setDeletingClientId('');
    }
  };

  const clientBrief = client => {
    const summary = client?.analysis?.summary;
    return summary || client?.description || client?.positioning || client?.sector || t('dashboard.clientNoBrief');
  };

  const linkedProjectsFor = clientId => projects
    .filter(project => project.client_brain_id === clientId)
    .sort((a, b) => String(b.updated_at || b.created_at || '').localeCompare(String(a.updated_at || a.created_at || '')));

  const handleQuickStartOpenProject = (project) => {
    setShowExistingClientQuickStart(false);
    setQuickStartClientId('');
    setSelectedProject(project);
    setProjects(prev => {
      const exists = prev.some(item => item.id === project.id);
      return exists ? prev.map(item => item.id === project.id ? project : item) : [project, ...prev];
    });
    setActiveView('content');
  };

  const handleQuickStartResume = (wizardState) => {
    setShowExistingClientQuickStart(false);
    setQuickStartClientId('');
    setWizardResumeData(wizardState);
    setActiveView('wizard');
  };

  const startRenameProject = (project) => {
    setEditingProjectId(project.id);
    setEditingProjectName(project.name || '');
  };

  const cancelRenameProject = () => {
    setEditingProjectId(null);
    setEditingProjectName('');
  };

  const renameProject = async (project) => {
    const nextName = editingProjectName.trim();
    if (!nextName) {
      toast.error(t('dashboard.renameProjectEmpty'));
      return;
    }
    if (nextName === project.name) {
      cancelRenameProject();
      return;
    }

    setRenamingProjectId(project.id);
    const tid = toast.loading(t('dashboard.renamingProject'));
    try {
      await api.put(`/projects/${project.id}`, { name: nextName });
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, name: nextName } : p));
      toast.success(t('dashboard.renameProjectSuccess'), { id: tid });
      cancelRenameProject();
    } catch (e) {
      toast.error(t('dashboard.renameProjectError') + ': ' + (e.response?.data?.detail || e.message), { id: tid });
    }
    setRenamingProjectId(null);
  };

  const activeProjects = projects.filter(p => !p.archived);
  const displayedProjects = projects.filter(project => Boolean(project.archived) === showArchived)
    .filter(project => [project.name, project.sector].filter(Boolean).join(' ').toLocaleLowerCase().includes(projectSearch.trim().toLocaleLowerCase()));
  const projectTotals = activeProjects.reduce((totals, project) => ({ contents: totals.contents + Number(project.content_count || 0), scheduled: totals.scheduled + Number(project.scheduled_count || 0) }), { contents: 0, scheduled: 0 });

  return (
    <div className="workspace-dashboard">
      <div className="workspace-dashboard-header flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="workspace-eyebrow">{t('workspace.creativeWorkspace')}</p>
          <h1 className="text-2xl md:text-3xl font-bold gradient-text" data-testid="dashboard-title">{t('dashboard.title')}</h1>
          <p className="text-[var(--text-secondary)] mt-1 text-xs md:text-sm">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button data-testid="new-project-btn" className="btn-gradient text-sm" onClick={() => setActiveView('wizard')}>
            <Plus weight="bold" size={16} /> {t('dashboard.newClient')}
          </button>
        </div>
      </div>

      <section className="workspace-overview" aria-label={t('workspace.overview')}>
        {[[UsersThree, t('dashboard.clientsTitle'), clients.length], [Archive, t('workspace.activeProjects'), activeProjects.length], [Article, t('dashboard.contents'), projectTotals.contents], [Check, t('status.scheduled'), projectTotals.scheduled]].map(([Icon, label, value]) => (
          <article key={label}><div><Icon size={16} /><span>{label}</span></div><strong>{loading ? '—' : value}</strong></article>
        ))}
      </section>
      <section className="mb-6 md:mb-8" data-testid="dashboard-client-management">
        <div className="flex items-end justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2">
              <UsersThree size={20} className="text-[var(--accent-purple)]" />
              <h2 className="text-lg font-semibold">{t('dashboard.clientsTitle')}</h2>
              <span className="badge purple text-[10px]">{clients.length}</span>
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-1">{t('dashboard.clientsSubtitle')}</p>
          </div>
          <ContextHelpButton articleId="dashboard-clients" label={t('dashboard.clientsHelp')} />
        </div>

        {loading ? (
          <div className="rounded-2xl border border-[var(--border-color)] p-5 text-sm text-[var(--text-muted)]">{t('common.loading')}</div>
        ) : clients.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[var(--border-color)] p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="dashboard-no-clients">
            <div>
              <p className="text-sm font-semibold">{t('dashboard.noClients')}</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">{t('dashboard.noClientsHint')}</p>
            </div>
            <button className="btn-gradient text-sm" onClick={() => setActiveView('wizard')}>
              <Plus size={15} /> {t('dashboard.newClient')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3" data-testid="dashboard-client-grid">
            {clients.map((client, index) => {
              const clientProjects = linkedProjectsFor(client.id);
              const activeClientProjects = clientProjects.filter(project => !project.archived);
              const latestProject = activeClientProjects[0] || clientProjects[0] || null;
              const additionalProjects = Math.max(0, clientProjects.length - (latestProject ? 1 : 0));
              return (
                <motion.article
                  key={client.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.04, 0.2) }}
                  className="rounded-2xl border border-[var(--border-color)] p-4 bg-[var(--bg-card)] min-w-0"
                  data-testid={`client-card-${client.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[9px] uppercase tracking-[.16em] text-[var(--accent-purple)] font-semibold">{t('dashboard.clientLabel')}</p>
                      <h3 className="text-base font-semibold truncate mt-0.5" title={client.name}>{client.name}</h3>
                      {client.sector && <p className="text-[10px] text-[var(--text-muted)] mt-1">{client.sector}</p>}
                    </div>
                    <button
                      type="button"
                      className="btn-ghost !p-2 text-rose-400"
                      onClick={() => deleteClient(client)}
                      disabled={deletingClientId === client.id}
                      title={t('dashboard.deleteClient')}
                      aria-label={t('dashboard.deleteClient')}
                      data-testid={`delete-client-${client.id}`}
                    >
                      {deletingClientId === client.id ? <span className="h-3 w-3 rounded-full border border-current border-t-transparent animate-spin" /> : <Trash size={14} />}
                    </button>
                  </div>

                  <div className="mt-4">
                    <p className="text-[9px] uppercase tracking-[.14em] text-[var(--text-muted)] font-semibold">{t('dashboard.whoIs')}</p>
                    <p className="text-xs text-[var(--text-secondary)] leading-relaxed mt-1 line-clamp-3" title={clientBrief(client)}>{clientBrief(client)}</p>
                  </div>

                  <div className="mt-4 rounded-xl border border-[var(--border-color)] bg-black/10 p-3">
                    <p className="text-[9px] uppercase tracking-[.14em] text-[var(--text-muted)] font-semibold">{t('dashboard.projectLabel')}</p>
                    {latestProject ? (
                      <button
                        type="button"
                        className="mt-1 w-full text-left group"
                        onClick={() => latestProject.status === 'draft' && (latestProject.wizard_step || 0) < 4 ? resumeWizard(latestProject) : openProject(latestProject)}
                        data-testid={`client-latest-project-${client.id}`}
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold truncate group-hover:text-fuchsia-300">{latestProject.name}</span>
                          <ArrowRight size={12} className="shrink-0 text-[var(--text-muted)] group-hover:text-fuchsia-300" />
                        </span>
                        <span className="block text-[9px] text-[var(--text-muted)] mt-1">
                          {latestProject.archived ? t('dashboard.archived') : t('dashboard.activeCampaign')}
                          {additionalProjects > 0 ? ` · +${additionalProjects} ${t('dashboard.otherCampaigns')}` : ''}
                        </span>
                      </button>
                    ) : (
                      <p className="text-xs text-[var(--text-muted)] mt-1">{t('dashboard.noCampaigns')}</p>
                    )}
                  </div>

                  <button
                    type="button"
                    className="btn-gradient text-xs w-full justify-center mt-3"
                    onClick={() => openClientCampaign(client.id)}
                    data-testid={`new-campaign-${client.id}`}
                  >
                    <Plus size={14} /> {t('dashboard.newCampaign')}
                  </button>
                </motion.article>
              );
            })}
          </div>
        )}
      </section>

      <div className="card workspace-projects">
        <div className="workspace-project-toolbar">
          <h2 className="text-lg font-semibold">{t('nav.project')} <ContextHelpButton articleId="dashboard-clients" label={t('dashboard.clientsHelp')} /></h2>
          <div className="workspace-project-controls">
            <div className="workspace-project-filters">
              <button type="button" className={!showArchived ? 'active' : ''} aria-pressed={!showArchived} onClick={() => setShowArchived(false)}>{t('workspace.active')}</button>
              <button type="button" className={showArchived ? 'active' : ''} aria-pressed={showArchived} onClick={() => setShowArchived(true)}>{t('workspace.archived')}</button>
            </div>
            <input className="input-dark" type="search" value={projectSearch} onChange={event => setProjectSearch(event.target.value)} placeholder={t('workspace.searchProjects')} aria-label={t('workspace.searchProjects')} data-testid="dashboard-project-search" />
          </div>
        </div>

        {loading ? (
          <p className="text-[var(--text-muted)] text-sm text-center py-8">{t('common.loading')}</p>
        ) : displayedProjects.length === 0 && (projectSearch || showArchived) ? (
          <p className="workspace-empty-search">{t('workspace.noMatchingProjects')}</p>
        ) : displayedProjects.length === 0 ? (
          <div className="empty-state">
            <Lightning size={64} className="empty-state-icon" />
            <h3 className="text-lg font-semibold mb-2 text-[var(--text-secondary)]">{t('dashboard.noProjects')}</h3>
            <p className="text-sm text-[var(--text-muted)] mb-6">{t('dashboard.noProjectsHint')}</p>
            <div className="flex gap-2 flex-wrap justify-center">
              <button className="btn-gradient" onClick={() => setActiveView('wizard')}>
                <Plus size={18} /> {t('dashboard.newCampaignOrProject')}
              </button>
            </div>
          </div>
        ) : (
          <div className="projects-grid">
            {displayedProjects.map((project, i) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                data-testid={`project-card-${project.id}`}
                className="card project-card"
              >
                {project.cover_url ? (
                  <div className="relative -mx-6 -mt-6 mb-4 h-32 rounded-t-[0.9rem] overflow-hidden">
                    <img src={resolveAssetUrl(project.cover_url)} alt="" className="w-full h-full object-cover" />
                    <label className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center cursor-pointer hover:bg-black/70 transition-colors">
                      <ImageSquare size={14} color="white" />
                      <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const file = e.target.files[0]; if (!file) return;
                        const fd = new FormData(); fd.append('file', file);
                        const { data } = await api.post(`/projects/${project.id}/cover`, fd);
                        setProjects(prev => prev.map(p => p.id === project.id ? { ...p, cover_url: data.cover_url } : p));
                      }} />
                    </label>
                  </div>
                ) : (
                  <label className="-mx-6 -mt-6 mb-4 h-24 rounded-t-[0.9rem] flex items-center justify-center cursor-pointer border-b border-[var(--border-color)]" style={{ background: 'var(--bg-secondary)' }}>
                    <div className="flex items-center gap-2 text-[var(--text-muted)] text-xs">
                      <ImageSquare size={16} /> {t('workspace.addCover')}
                    </div>
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files[0]; if (!file) return;
                      const fd = new FormData(); fd.append('file', file);
                      const { data } = await api.post(`/projects/${project.id}/cover`, fd);
                      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, cover_url: data.cover_url } : p));
                    }} />
                  </label>
                )}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    {editingProjectId === project.id ? (
                      <div className="flex items-center gap-2 mb-1">
                        <input
                          className="input-dark text-sm py-2 flex-1"
                          value={editingProjectName}
                          onChange={e => setEditingProjectName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') renameProject(project);
                            if (e.key === 'Escape') cancelRenameProject();
                          }}
                          autoFocus
                          style={{ paddingLeft: '0.75rem' }}
                        />
                        <button
                          className="btn-ghost p-2"
                          onClick={() => renameProject(project)}
                          disabled={renamingProjectId === project.id}
                          title={t('common.save')}
                        >
                          {renamingProjectId === project.id
                            ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                            : <Check size={14} />}
                        </button>
                        <button className="btn-ghost p-2" onClick={cancelRenameProject} title={t('common.cancel')}>
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold truncate">{project.name}</h3>
                        <button
                          className="btn-ghost p-1.5 flex-shrink-0"
                          title={t('dashboard.renameProject')}
                          onClick={() => startRenameProject(project)}
                        >
                          <PencilSimple size={13} />
                        </button>
                      </div>
                    )}
                    <p className="text-sm text-[var(--text-secondary)]">{project.sector}</p>
                  </div>
                  <span className={`badge ${project.status === 'active' ? 'green' : project.status === 'completed' ? 'purple' : 'orange'}`}>
                    {t(`status.${project.status}`, { defaultValue: project.status })}
                  </span>
                </div>
                <div className="flex items-center gap-3 flex-wrap text-sm text-[var(--text-secondary)] mb-4">
                  <span className="flex items-center gap-1"><Article size={14} /> {project.content_count || 0} {t('dashboard.contents')}</span>
                  <span className="badge text-[10px] orange">{project.scheduled_count || 0} {t('status.scheduled')}</span>
                  <span className="badge text-[10px] purple">{project.draft_count || 0} {t('status.draft')}</span>
                </div>
                <div className="flex gap-2">
                  {project.status === 'draft' && (project.wizard_step || 0) < 4
                    ? <button className="btn-gradient text-xs md:text-sm py-2 px-3 flex-1" onClick={() => resumeWizard(project)}>▶ {t('dashboard.resumeWizard')}</button>
                    : <button className="btn-ghost text-xs md:text-sm py-2 px-3 flex-1" onClick={() => openProject(project)}><Eye size={14} /> {t('dashboard.openProject')}</button>
                  }
                  <button className="btn-ghost text-xs md:text-sm py-2 px-3" title={t(project.archived ? 'dashboard.unarchiveProject' : 'dashboard.archiveProject')} onClick={() => archiveProject(project.id, project.archived)}><Archive size={14} /></button>
                  <button className="btn-ghost text-xs md:text-sm py-2 px-3" title={t('dashboard.deleteProject')} onClick={() => deleteProject(project.id)}><Trash size={14} /></button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {showExistingClientQuickStart && (
        <ExistingClientQuickStart
          api={api}
          initialClientId={quickStartClientId}
          onClose={() => { setShowExistingClientQuickStart(false); setQuickStartClientId(''); }}
          onOpenProject={handleQuickStartOpenProject}
          onResumeWizard={handleQuickStartResume}
        />
      )}
    </div>
  );
}

